import { EmojiPickerDropdown } from "@/components/emoji/EmojiPickerDropdown.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import {
  useJobEditData,
  useJobEditForm,
} from "@/pages/recruit/jobs/edit/components/job-edit.type.ts";
import { JobEditAi } from "@/pages/recruit/jobs/edit/components/JobEditAi.tsx";
import { JobEditField } from "@/pages/recruit/jobs/edit/components/JobEditField.tsx";
import { useJobArchivingReasons } from "@/queries/recruit/use-job-archiving-reasons.query.ts";
import { JobStatus, JobStatusItems } from "@/types/recruit/job.types.ts";
import { Avatar, Box, FontAwesomeIcon, Select, SelectItem, Stack, Title } from "@skailer-com/uikit";
import { useMemo } from "react";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function JobEditDetails() {
  const {
    control,
    watch,
    formState: { errors },
  } = useJobEditForm();
  const { t } = useTranslation();
  const { getSectionTitle } = useCustomFieldsHook();
  const { sections } = useJobEditData();
  const watchStatus = watch("status");
  const { data: jobArchivingReasons, isLoading: isJobArchivingReasonsLoading } =
    useJobArchivingReasons();

  const jobArchivingReasonOptions: SelectItem[] = useMemo(
    () => (jobArchivingReasons ?? []).map((x) => ({ id: x.id, title: x.title })),
    [jobArchivingReasons]
  );

  return (
    <>
      <Stack className={"card-panel bg-light/80 backdrop-blur-md"} gap={"2xl"}>
        <Controller
          control={control}
          render={({ field: { value, onChange } }) => (
            <EmojiPickerDropdown
              onEmojiSelect={(emoji) => {
                onChange(emoji);
              }}
            >
              <Box className={"relative w-41 h-41 group cursor-pointer "}>
                <Avatar variant={"opaque"} url={undefined} emoji={value} size={"2xl"} />
                <Box
                  className={
                    "bg-dark/20 opacity-0 group-hover:opacity-100 absolute inset-0 rounded-lg flex items-center justify-center"
                  }
                >
                  <FontAwesomeIcon
                    icon={"fa-light fa-icons"}
                    className={"text-[74px] text-light "}
                  />
                </Box>
              </Box>
            </EmojiPickerDropdown>
          )}
          name={"emoji"}
        />

        <Stack gap={"lg"}>
          <Title header={t("ats:job")} caption={t("ats:job_enter_description")} size={"lg"} />
          <Stack>
            <Controller
              control={control}
              name={"status"}
              render={({ field: { value, onChange } }) => (
                <Select
                  label={t("common:status")}
                  options={JobStatusItems()}
                  emptyTitle={t("common:select")}
                  value={value}
                  onChange={onChange}
                  error={errors.status?.message}
                />
              )}
            />
            {watchStatus === JobStatus.archived && (
              <Controller
                control={control}
                name={`job_archiving_reason_id`}
                render={({ field: { value, onChange } }) => (
                  <Select
                    isClearable
                    label={t("ats:job_archiving_reason")}
                    options={jobArchivingReasonOptions}
                    isLoading={isJobArchivingReasonsLoading}
                    emptyTitle={t("common:select")}
                    searchOptions={jobArchivingReasonOptions}
                    value={value}
                    onChange={onChange}
                    error={errors.job_archiving_reason_id?.message}
                  />
                )}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
      {sections.map((section) => (
        <Stack key={section.id} className={"card-panel"} gap={"2xl"}>
          <Title header={getSectionTitle(section)} size={"md"} />
          <Stack>
            {section.fields.map((field) => (
              <JobEditField key={field.id} field={field} />
            ))}
          </Stack>
        </Stack>
      ))}
      <JobEditAi />
    </>
  );
}
